import { useEffect, useState } from "react";
import type * as React from "react";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import {
  getMembershipInvites,
  type MembershipInviteRecord
} from "apis/v3/authApi";
import { PATHS } from "constants/pathConstants";
import PageTitle from "utils/PageTitle";
import GradientFramedLayout from "components/v3/layouts/GradientFramedLayout";
import BreadcrumbTrail from "components/v3/sharedComponents/BreadcrumbTrail";
import AccountSecurityCallout from "components/v3/pages/account/AccountManagementPage/AccountSecurityCallout";
import GLButton from "components/v3/sharedComponents/GLButton";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import { COLORS } from "components/v3/Theme/colors";
import InvitesCard from "./InvitesCard";

const BILLING_AND_PAYMENTS_HEADING = "Billing & Payments";
const ERROR_MESSAGE = "Something went wrong. Please reload.";
const EMPTY_MESSAGE = "Nothing to show.";

type InvitesFetchStatus = "loading" | "error" | "success";

const statusCardSx = {
  backgroundColor: COLORS.BACKGROUND_BLUE,
  borderRadius: "12px",
  boxSizing: "border-box",
  maxWidth: "100%",
  minWidth: 0,
  padding: 2.5,
  width: "100%"
} as const;

const BillingAndPaymentsPage: React.FC = () => {
  const [reloadToken, setReloadToken] = useState(0);
  const [status, setStatus] = useState<InvitesFetchStatus>("loading");
  const [invites, setInvites] = useState<MembershipInviteRecord[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");

    getMembershipInvites()
      .then((response) => {
        if (cancelled) {
          return;
        }
        if (response && !axios.isAxiosError(response)) {
          setInvites(response.data?.data?.invites ?? []);
          setStatus("success");
          return;
        }
        setInvites([]);
        setStatus("error");
      })
      .catch(() => {
        if (!cancelled) {
          setInvites([]);
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  PageTitle(BILLING_AND_PAYMENTS_HEADING);

  const paidInvites = invites.filter((invite) => invite.isPaid);
  const unpaidInvites = invites.filter((invite) => !invite.isPaid);

  const renderContent = () => {
    if (status === "loading") {
      return (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            minHeight: 240,
            py: 4,
            width: "100%"
          }}
        >
          <LoadingAnimation />
        </Box>
      );
    }

    if (status === "error") {
      return (
        <Box sx={statusCardSx}>
          <Typography
            variant="body2"
            sx={{ color: COLORS.DARK_GRAY_2, textAlign: "center" }}
          >
            {ERROR_MESSAGE}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <GLButton
              id="reload-invites"
              text="Reload"
              onClick={() => setReloadToken((token) => token + 1)}
            />
          </Box>
        </Box>
      );
    }

    if (invites.length === 0) {
      return (
        <Box sx={statusCardSx}>
          <Typography
            variant="body2"
            sx={{ color: COLORS.DARK_GRAY_2, textAlign: "center" }}
          >
            {EMPTY_MESSAGE}
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {paidInvites.length > 0 && (
          <InvitesCard title="Gifts" invites={paidInvites} />
        )}
        {unpaidInvites.length > 0 && (
          <InvitesCard title="Invitations" invites={unpaidInvites} />
        )}
      </>
    );
  };

  return (
    <GradientFramedLayout showHeader={true}>
      <Box
        sx={{
          alignItems: "stretch",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          marginX: "auto",
          maxWidth: { md: 840, xs: "100%" },
          minWidth: 0,
          paddingBottom: "40px",
          paddingTop: { md: 3, xs: 2 },
          paddingX: { md: 3, xs: 2 },
          width: "100%"
        }}
      >
        <Typography
          variant="h4_primary"
          component="h1"
          sx={{ fontWeight: 700, textAlign: "left", width: "100%" }}
        >
          {BILLING_AND_PAYMENTS_HEADING}
        </Typography>

        <BreadcrumbTrail
          segments={[
            { label: "Account", to: PATHS.ACCOUNT_MANAGEMENT },
            { label: BILLING_AND_PAYMENTS_HEADING }
          ]}
        />

        {renderContent()}

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            maxWidth: "100%",
            minWidth: 0,
            width: "100%"
          }}
        >
          <Box
            sx={{
              boxSizing: "border-box",
              maxWidth: { md: "66%", xs: "100%" },
              minWidth: 0,
              width: { md: "66%", xs: "100%" }
            }}
          >
            <AccountSecurityCallout />
          </Box>
        </Box>
      </Box>
    </GradientFramedLayout>
  );
};

export default BillingAndPaymentsPage;
